import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"

import { AlertsStore } from "./store"
import { useGetAlertsColumns } from "./hooks/useGetAlertsColumns"

import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { GlobalStore } from "src/store"
import { ListPage } from "src/components/ListPage"
import { DataGridProTable } from "src/components/Table/DataGridPro"
import {
    FilterModel,
    IAdvancedOperations,
    SortModel,
} from "src/types/data-grid-pro"
import {
    getFilterModelForRepository,
    getSortModelForRepository,
} from "src/lib/data-grid-pro"
import { Repository } from "src/types/channel"
import { useInitializer } from "src/lib/initializer"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { ConfirmModal } from "src/modals/confirm"
import { AlertDetailModal } from "src/modals/alert-detail"
import { IAlertListItem } from "src/views/alerts/types"

const repository: Repository = "alert"

const View = observer(() => {
    const store = useStore(AlertsStore)
    const gstore = useStore(GlobalStore)
    const columns = useGetAlertsColumns()
    const { id } = useParams<{ id?: string }>()
    const navigate = useNavigate()

    const advancedOperationsModel: IAdvancedOperations = useMemo(
        () => ({
            pagination: "server",
            filtering: "server",
            sorting: "server",
        }),
        [],
    )

    const advanceQuery = useMemo(() => {
        const filter = getFilterModelForRepository(
            repository,
            gstore.session.dataGridFilterModel,
        )
        const sort = getSortModelForRepository(
            repository,
            gstore.session.dataGridSortModel,
        )
        return { sort, filter }
    }, [gstore.session.dataGridFilterModel, gstore.session.dataGridSortModel])

    const initialized = useInitializer(async () => {
        const accessGroupId =
            gstore.session.accessGroupId ?? DEFAULT_ACCESS_GROUP.id
        await store.init(accessGroupId, advanceQuery)
    }, [gstore.session.accessGroupId, advanceQuery])

    const openAlertModal = (alertId: number) => {
        gstore.modals.open(() => <AlertDetailModal id={alertId} />, {
            onClose: () => navigate("/alert"),
        })
    }

    useEffect(() => {
        if (!initialized) return
        if (id != null && !isNaN(Number(id))) {
            openAlertModal(Number(id))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized, id])

    useEffect(() => {
        return () => {
            store.dispose()
        }
    }, [store])

    const handleNewAlertClick = useCallback(() => {
        gstore.modals.open(() => <AlertDetailModal />)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleEditItem = useCallback((item: IAlertListItem) => {
        navigate(`/alert/${item.id}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDuplicateItem = useCallback((item: IAlertListItem) => {
        gstore.modals.open(
            () => <AlertDetailModal id={item.id} mode="Copy" />,
            {
                onClose: () => navigate("/alert"),
            },
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDeleteItem = useCallback((item: IAlertListItem) => {
        gstore.modals.open(
            () => (
                <ConfirmModal
                    onConfirm={async (confirmed) => {
                        if (confirmed && item.id != null) {
                            await store.deleteAlert(item.id)
                        }
                    }}
                    title={t`alerts-view.are-you-sure`}
                    content={`${t`alerts-view.confirm-delete-item`} ${
                        item.title
                    }`}
                />
            ),
            {
                variant: "slide-up-w600",
            },
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const header = useMemo(
        () => ({
            header: t`alerts-view.header`,
            breadcrumbs: [t`alerts-view.header`],
            createOptions: {
                item: t`alerts-view.add-alert`,
                onClick: handleNewAlertClick,
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const handleFilterChange = useCallback(
        async (model: FilterModel) => {
            const sort = getSortModelForRepository(
                repository,
                gstore.session.dataGridSortModel,
            )
            await store.query({ sort, filter: model })
        },
        [gstore.session.dataGridSortModel, store],
    )

    const handleSortChange = useCallback(
        async (model: SortModel) => {
            const filter = getFilterModelForRepository(
                repository,
                gstore.session.dataGridFilterModel,
            )
            await store.query({ filter, sort: model })
        },
        [gstore.session.dataGridFilterModel, store],
    )

    const rowActionsRenderer = useCallback(
        (item: IAlertListItem) => [
            {
                text: t`alerts-view.edit-alert`,
                onClick: () => handleEditItem(item),
                hidden: item.access_type === "READ",
            },
            {
                text: t`alerts-view.duplicate-alert` ?? "Duplicate",
                onClick: () => handleDuplicateItem(item),
            },
            {
                text: t`alerts-view.delete-alert`,
                onClick: () => handleDeleteItem(item),
                hidden: item.access_type === "READ",
            },
        ],
        [handleEditItem, handleDuplicateItem, handleDeleteItem],
    )

    return (
        <ListPage header={header} loading={!initialized}>
            <DataGridProTable
                paginator={store.alerts}
                data={store.alerts.items}
                columns={columns}
                advancedOperations={advancedOperationsModel}
                rowActionsRenderer={rowActionsRenderer}
                repository={repository}
                loading={!initialized}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClickEvent={(params) => {
                    const row = params.row as IAlertListItem
                    if (row.access_type !== "READ") {
                        handleEditItem(row)
                    }
                }}
            />
        </ListPage>
    )
})

export const AlertsView = () => (
    <StoreProvider Store={AlertsStore}>
        <View />
    </StoreProvider>
)
